'use client';

import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';

export default function HeroVideo() {
  const t = useTranslations('common');
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [videoEnded, setVideoEnded] = useState(false);
  const [hlsSupported, setHlsSupported] = useState(false);
  const [muxError, setMuxError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [hlsLoaded, setHlsLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<any>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Get Mux HLS URL from environment
  const muxHlsUrl = process.env.NEXT_PUBLIC_HERO_HLS_URL;

  useEffect(() => {
    // Set client-side flag
    setIsClient(true);
    
    // Detect mobile devices - only on client side
    const checkMobile = () => {
      if (typeof window === 'undefined') return;
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
  }, []);

  // Load HLS.js dynamically
  useEffect(() => {
    if (!isClient) return;

    const loadHls = async () => {
      try {
        const HlsModule = await import('hls.js');
        const HlsClass = HlsModule.default;
        setHlsSupported(HlsClass.isSupported());
        setHlsLoaded(true);
      } catch (error) {
        console.error('Failed to load HLS.js:', error);
        setHlsLoaded(true);
      }
    };

    loadHls();
  }, [isClient]);

  useEffect(() => {
    // Only run after HLS is loaded and on client side
    if (!isClient || !hlsLoaded) return;

    if (videoRef.current) {
      // Try Mux HLS first if available
      if (muxHlsUrl && hlsSupported) {
        initializeMuxHls();
      } else if (hlsSupported) {
        // Fallback to local HLS
        initializeHls();
      } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        setHlsSupported(true);
        videoRef.current.src = muxHlsUrl || '/hls/1022.m3u8';
      } else {
        // Fallback to MP4
        setHlsSupported(false);
        videoRef.current.src = '/videos/1022.mp4';
      }
    }

    return () => {
      // Cleanup HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isClient, hlsLoaded, hlsSupported, muxHlsUrl]);

  const initializeMuxHls = async () => {
    if (!videoRef.current || !muxHlsUrl) return;

    try {
      const HlsModule = await import('hls.js');
      const HlsClass = HlsModule.default;

      const hls = new HlsClass({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        maxBufferSize: 60 * 1000 * 1000, // 60MB
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.2,
        nudgeMaxRetry: 5,
        maxFragLookUpTolerance: 0.25,
        progressive: false,
        debug: false,
        // Mux-optimized settings
        abrEwmaDefaultEstimate: 1000000,
        abrBandWidthFactor: 0.95,
        abrBandWidthUpFactor: 0.7,
        abrMaxWithRealBitrate: true,
        startLevel: -1,
      });

      hlsRef.current = hls;

      // Bind HLS events
      hls.on(HlsClass.Events.MEDIA_ATTACHED, () => {
        console.log('Mux HLS attached to video element');
        hls.loadSource(muxHlsUrl);
      });

      hls.on(HlsClass.Events.MANIFEST_PARSED, (event: any, data: any) => {
        console.log('Mux HLS manifest loaded, found ' + data.levels.length + ' quality levels');
        setVideoLoaded(true);
        setIsLoading(false);
        setupIntersectionAutoplay();
      });

      hls.on(HlsClass.Events.ERROR, (event: any, data: any) => {
        console.error('Mux HLS error:', data);
        if (data.fatal) {
          console.log('Mux HLS failed, falling back to local HLS');
          setMuxError(true);
          initializeHls();
        }
      });

      hls.on(HlsClass.Events.LEVEL_SWITCHED, (event: any, data: any) => {
        console.log('Mux quality level switched to:', data.level);
      });

      // Attach HLS to video element
      hls.attachMedia(videoRef.current);
    } catch (error) {
      console.error('Failed to initialize Mux HLS:', error);
      initializeHls();
    }
  };

  const initializeHls = async () => {
    if (!videoRef.current) return;

    try {
      const HlsModule = await import('hls.js');
      const HlsClass = HlsModule.default;

      const hls = new HlsClass({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        maxBufferSize: 60 * 1000 * 1000, // 60MB
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.2,
        nudgeMaxRetry: 5,
        maxFragLookUpTolerance: 0.25,
        progressive: false,
        debug: false,
        abrEwmaDefaultEstimate: 500000,
        abrBandWidthFactor: 0.95,
        abrBandWidthUpFactor: 0.7,
        abrMaxWithRealBitrate: true,
        startLevel: -1,
      });

      hlsRef.current = hls;

      // Bind HLS events
      hls.on(HlsClass.Events.MEDIA_ATTACHED, () => {
        console.log('Local HLS attached to video element');
        hls.loadSource('/hls/1022.m3u8');
      });

      hls.on(HlsClass.Events.MANIFEST_PARSED, (event: any, data: any) => {
        console.log('Local HLS manifest loaded, found ' + data.levels.length + ' quality levels');
        setVideoLoaded(true);
        setIsLoading(false);
        setupIntersectionAutoplay();
      });

      hls.on(HlsClass.Events.ERROR, (event: any, data: any) => {
        console.error('Local HLS error:', data);
        if (data.fatal) {
          switch (data.type) {
            case HlsClass.ErrorTypes.NETWORK_ERROR:
              console.log('Network error, trying to recover...');
              hls.startLoad();
              break;
            case HlsClass.ErrorTypes.MEDIA_ERROR:
              console.log('Media error, trying to recover...');
              hls.recoverMediaError();
              break;
            default:
              console.error('Fatal error, falling back to MP4');
              hls.destroy();
              setVideoError(true);
              setIsLoading(false);
              if (videoRef.current) {
                videoRef.current.src = '/videos/1022.mp4';
              }
              break;
          }
        }
      });

      hls.on(HlsClass.Events.LEVEL_SWITCHED, (event: any, data: any) => {
        console.log('Quality level switched to:', data.level);
      });

      // Attach HLS to video element
      hls.attachMedia(videoRef.current);
    } catch (error) {
      console.error('Failed to initialize local HLS:', error);
      setVideoError(true);
      setIsLoading(false);
      if (videoRef.current) {
        videoRef.current.src = '/videos/1022.mp4';
      }
    }
  };

  const setupIntersectionAutoplay = () => {
    if (!videoRef.current) return;

    observerRef.current = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (!videoRef.current) return;
        if (entry.isIntersecting) {
          if (videoRef.current.ended) {
            return;
          }
          try {
            await videoRef.current.play();
            setIsPlaying(true);
          } catch (err) {
            console.log('Intersection play blocked');
          }
        } else {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.35 }
    );

    observerRef.current.observe(videoRef.current);
  };

  const handleVideoError = (e: any) => {
    console.error('Video error:', e);
    setVideoError(true);
    setIsLoading(false);
  };

  const handleVideoCanPlay = () => {
    console.log('Video can play');
    setVideoLoaded(true);
    setIsLoading(false);
  };

  const handleVideoEnded = () => {
    console.log('Video ended - freezing on last frame');
    setIsPlaying(false);
    setVideoEnded(true);
  };

  const toggleAudio = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
      console.log('Audio toggled:', newMutedState ? 'muted' : 'unmuted');
    }
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
    setVideoEnded(false);
    console.log('Video started playing');
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
    console.log('Video paused');
  };

  const restartVideo = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setVideoEnded(false);
    }
  };

  // Show loading state while client-side hydration is happening
  if (!isClient) {
    return (
      <div className="relative w-full h-[90vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-logo-green-light to-logo-green flex items-center justify-center">
          <img 
            // src="/images/hero-1.jpg" 
            src="/images/school-building2.jpg" 
            alt="SN Cholmcille - Baile Na Finne" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl lg:text-6xl font-bold mb-4 drop-shadow-lg">
                SN Cholmcille
              </h1>
              <p className="text-xl lg:text-2xl drop-shadow-lg">
                Baile Na Finne
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[90vh] overflow-hidden">
      {/* Video Background - Works on both desktop and mobile */}
      {!videoError && (
        <video
          ref={videoRef}
          autoPlay
          loop={false}
          muted={isMuted}
          playsInline
          preload="metadata"
          // poster="/images/hero-1.jpg"
          poster="/images/school-building2.jpg"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onCanPlay={handleVideoCanPlay}
          onError={handleVideoError}
          onLoadStart={() => {
            console.log('Video load started');
            setIsLoading(true);
          }}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onEnded={handleVideoEnded}
          onWaiting={() => console.log('Video waiting for data')}
        >
          {/* Mux HLS source (primary) */}
          {muxHlsUrl && hlsSupported && <source src={muxHlsUrl} type="application/x-mpegURL" />}
          {/* Local HLS source (fallback) */}
          {hlsSupported && <source src="/hls/1022.m3u8" type="application/x-mpegURL" />}
          {/* MP4 fallback */}
          <source src="/videos/1022.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          Your browser does not support the video tag.
        </video>
      )}

      {/* Mobile fallback - just show the hero image */}
      {isMobile && videoError && (
        <div className="absolute inset-0 bg-gradient-to-br from-logo-green-light to-logo-green flex items-center justify-center">
          <img 
            // src="/images/hero-1.jpg"
            src="/images/school-building2.jpg"
            alt="SN Cholmcille - Baile Na Finne" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl lg:text-6xl font-bold mb-4 drop-shadow-lg">
                SN Cholmcille
              </h1>
              <p className="text-xl lg:text-2xl drop-shadow-lg">
                Baile Na Finne
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Audio Toggle Button */}
      <div className="absolute bottom-6 right-6 z-10">
        <button
          onClick={toggleAudio}
          className="p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent backdrop-blur-sm"
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          title={isMuted ? 'Click to unmute' : 'Click to mute'}
        >
          {isMuted ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            </svg>
          )}
        </button>
      </div>

      {/* Loading Spinner */}
      {isLoading && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
            <p className="text-white text-lg font-medium backdrop-blur-sm bg-black bg-opacity-20 px-4 py-2 rounded-lg">
              Loading Video...
            </p>
          </div>
        </div>
      )}

      {/* Play/Restart Button (shown when video is not playing) */}
      {!isPlaying && videoLoaded && !isLoading && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <button
            onClick={videoEnded ? restartVideo : () => videoRef.current?.play()}
            className="p-4 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent backdrop-blur-sm"
            aria-label={videoEnded ? 'Restart video' : 'Play video'}
            title={videoEnded ? 'Click to restart video' : 'Click to play video'}
          >
            {videoEnded ? (
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.001 0 01-15.357-2m15.357 2H15" />
              </svg>
            ) : (
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </button>
        </div>
      )}
      
      {/* Fallback content if video fails to load or while loading */}
      {(!videoLoaded || videoError) && !isMobile && (
        <div className="absolute inset-0 bg-gradient-to-br from-logo-green-light to-logo-green flex items-center justify-center">
          <img 
            // src="/images/hero-1.jpg" 
            src="/images/school-building2.jpg" 
            alt="SN Cholmcille - Baile Na Finne" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl lg:text-6xl font-bold mb-4 drop-shadow-lg">
                SN Cholmcille
              </h1>
              <p className="text-xl lg:text-2xl drop-shadow-lg">
                Baile Na Finne
              </p>
            </div>
          </div>
          {videoError && (
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded text-sm">
              Video failed to load. Check console for details.
            </div>
          )}
          {!videoLoaded && !videoError && (
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded text-sm">
              Loading video...
            </div>
          )}
        </div>
      )}
      
      {/* Video controls accessibility */}
      <div className="sr-only">
        <p>Background video: Viva La Vida - playing automatically, muted, and will freeze on last frame</p>
      </div>
    </div>
  );
}
